import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { ArrowBack, ArrowForward, Refresh } from '@mui/icons-material';
import axios from 'axios';
import { format, subMonths } from 'date-fns';

const UserSignals = ({ userID, parentID, userKind }) => {
  const { t } = useTranslation();
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(subMonths(new Date(), 1));
  const [toDate, setToDate] = useState(new Date());
  const [searchText, setSearchText] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage] = useState(20);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const fetchSignals = async () => {
    setLoading(true);
    setError(null);
    setSnackbarOpen(false);
    try {
      const formattedFrom = format(fromDate, 'yyyy-MM-dd');
      const formattedTo = format(toDate, 'yyyy-MM-dd');
      const payload = {
        userid: userID,
        parentid: parentID,
        startDate: formattedFrom,
        endDate: formattedTo,
        signalKind: userKind,
        PageNumber: pageNumber,
        RowsPerPage: rowsPerPage,
      };
      const apiUrl = 'https://copyapi.avangar.ir/api/forex/UserSignalReport';
      const response = await axios.post(apiUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      if (response.data?.Message === 'No signals found.') {
        setSignals([]);
        setSnackbarMessage(t('signals.noData'));
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      } else {
        setSignals(response.data || []);
        if (!response.data || response.data.length === 0) {
          setSnackbarMessage(t('signals.noData'));
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
        }
      }
    } catch (err) {
      console.error('UserSignals error:', err);
      let errorMessage = err.response?.data?.Message || t('signals.error');
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setSignals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSignals();
  }, [userID, parentID, userKind, pageNumber]);

  const handleRefresh = () => {
    setPageNumber(1);
    fetchSignals();
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const handleNextPage = () => {
    if (signals.length === rowsPerPage) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const filteredSignals = useMemo(() => {
    if (!searchText.trim()) return signals;
    const searchLower = searchText.toLowerCase();
    const filtered = signals.filter((signal) => {
      const signalData = JSON.parse(signal.JsonText || '{}');
      const fieldsToSearch = [
        signal.ReceivedDatetime || '',
        signal.ReceiptHistoryDateTime || '',
        String(signal.Profits || '0'),
        signalData.Symbol || '',
        String(signalData.Ticket || ''),
        signalData.Action || '',
        String(signalData.Price || ''),
        String(signalData.Profit || ''),
        ...Object.entries(signalData)
          .filter(([key]) => key !== 'Symbol' && key !== 'Ticket' && key !== 'OpenTime' && key !== 'Action' && key !== 'Price' && key !== 'Profit')
          .map(([key, value]) => `${key}: ${value}`),
      ];
      return fieldsToSearch.some((field) => field.toLowerCase().includes(searchLower));
    });
    if (filtered.length === 0 && searchText.trim()) {
      setSnackbarMessage(t('signals.noResults'));
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
    }
    return filtered;
  }, [signals, searchText, t]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 2 }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          {/* Row 1: DatePickers */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              mb: 2,
              '@media (min-width: 1280px)': {
                justifyContent: 'flex-start',
              },
            }}
          >
            <DatePicker
              label={t('signals.fromDate')}
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              format="yyyy-MM-dd"
              slotProps={{
                textField: { size: 'small' },
              }}
              sx={{
                width: '133px',
                '@media (min-width: 1280px)': {
                  width: '152px', // 133px + 15% = ~152px
                },
              }}
            />
            <DatePicker
              label={t('signals.toDate')}
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
              format="yyyy-MM-dd"
              slotProps={{
                textField: { size: 'small' },
              }}
              sx={{
                width: '133px',
                '@media (min-width: 1280px)': {
                  width: '152px', // 133px + 15% = ~152px
                },
              }}
            />
          </Box>
          {/* Row 2: Search, Refresh, and Pagination */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
              '@media (min-width: 1280px)': {
                flexWrap: 'nowrap',
              },
            }}
          >
            <TextField
              label={t('signals.search')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              sx={{ width: '168px' }}
            />
            <IconButton onClick={handleRefresh} aria-label={t('signals.refresh')}>
              <Refresh />
            </IconButton>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                ml: 'auto',
              }}
            >
              <IconButton
                onClick={handlePreviousPage}
                disabled={pageNumber === 1}
                aria-label={t('signals.previous')}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="body2">
                {t('signals.page')} {pageNumber}
              </Typography>
              <IconButton
                onClick={handleNextPage}
                disabled={signals.length < rowsPerPage}
                aria-label={t('signals.next')}
              >
                <ArrowForward />
              </IconButton>
            </Box>
          </Box>
        </Paper>
        <Paper
          sx={{
            maxHeight: '400px',
            overflowY: 'auto',
            p: 2,
            bgcolor: 'rgba(255,255,255,0.95)',
            borderRadius: 2,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : filteredSignals.length > 0 ? (
            filteredSignals.map((signal, index) => {
              const signalData = JSON.parse(signal.JsonText || '{}');
              const remainingFields = Object.entries(signalData)
                .filter(([key]) => key !== 'Symbol' && key !== 'Ticket' && key !== 'OpenTime' && key !== 'Action' && key !== 'Price' && key !== 'Profit')
                .map(([key, value]) => `${key}: ${value}`)
                .join(', ');
              const backgroundColor =
                signal.Profits > 0 ? '#e8f5e9' : signal.Profits < 0 ? '#ffebee' : '#f9f9f9';
              return (
                <Box
                  key={index}
                  sx={{
                    mt: 1,
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    bgcolor: backgroundColor,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      <strong>{t('signals.createdAt')}:</strong>{' '}
                      {signal.ReceivedDatetime || '-'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('profits')}:</strong>{' '}
                      {signal.Profits || '0'}
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    <strong>{t('signals.receivedAt')}:</strong>{' '}
                    {signal.ReceiptHistoryDateTime || '-'}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">
                      <strong>{t("signals.ticket")}:</strong> {signalData.Ticket || "-"},{" "}
                      <strong>{t("signals.action")}:</strong> {signalData.Action || "-"}
                    </Typography>
                    {signalData.Profit !== undefined && (
                      <Typography variant="body2">
                        <strong>{t("signals.masterProfits")}:</strong>{" "}
                        {signalData.Profit}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2">
                    <strong>{t("signals.symbol")}:</strong> {signalData.Symbol || "-"},{" "}
                    <strong>{t("signals.price")}:</strong> {signalData.Price || "-"}
                  </Typography>
                  {remainingFields && (
                    <Typography variant="body2">{remainingFields}</Typography>
                  )}
                </Box>
              );
            })
          ) : (
            <Typography>{t('signals.noData')}</Typography>
          )}
        </Paper>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              animation: 'fadeInDown 0.5s ease-out forwards',
            },
          }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: '100%',
              bgcolor: snackbarSeverity === 'success' ? '#e8f5e9' : '#ffebee',
              color: snackbarSeverity === 'success' ? '#2e7d32' : '#c62828',
              '& .MuiAlert-icon': {
                color: snackbarSeverity === 'success' ? '#4caf50' : '#d32f2f',
              },
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default UserSignals;